import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'uxd-design-system-components';
import './ValueCardFP.scss';
import ValueCardProps from '../../types/components/ValueCardProps';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';
import LearnMoreCards from '../LearnMoreCards/LearnMoreCards';
import LocaleContext from '../../contexts/LocaleContext';
import getText from '../../assets/locale/locale';
// eslint-disable-next-line import/no-unresolved
import * as images from 'url:../../assets/imagesFP/*.png';
import UserContext from '../../contexts/UserContext';
import UserPersonaTypes from '../../constants/UserPersonaTypes';

const ValueCardFP = (props: ValueCardProps) => {
  const {
    valueStatement,
    valueStatementId,
    learnMoreContent,
    learnMoreLinks,
    isLoading,
    displayLearnMoreButton,
    rank,
    disabled,
    cardClassName,
    setCardClassName,
    setShowAdditionalInfoModal,
    setShowCustomPriorityEditModal,
    isEditable,
    setEditModalCurrentCardValue,
    infoModalContent,
    setInfoModalContent,
  } = props;

  const locId = useContext(LocaleContext);
  const userPersona = useContext(UserContext);
  const [displayLearnMoreContent, setDisplayLearnMoreContent] = useState(false);
  const SPACE = ' ';
  const ENTER = 'Enter';
  const ESCAPE = 'Escape';
  const personaTypeArrayUS = Array.of(
    UserPersonaTypes.WORD_POWER,
    UserPersonaTypes.NEWS_LETTER,
    UserPersonaTypes.PERSONAL_LINK,
    UserPersonaTypes.PERSONAL_LINK_FA_PROFILE,
    UserPersonaTypes.FINANCIAL_FOUNDATION,
    UserPersonaTypes.DIRECT_URL,
  );


  useEffect(() => {
    if (displayLearnMoreContent) {
      setCardClassName('value-card-fp value-card-disabled');
    }
  }, []);

  useEffect(() => {
    if (!displayLearnMoreContent) {
      document.body.classList.remove('verification-multi-modal');
    }
  }, [displayLearnMoreContent]);

  const learnMoreClickHandler = () => {
    addDataLayerTrigger('learn-more-content', {});
    setDisplayLearnMoreContent(true);
    document.body.classList.add('verification-multi-modal');
  };

  const learnMoreKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ENTER || e.key === SPACE) {
      addDataLayerTrigger('learn-more-content', {});
      setDisplayLearnMoreContent(true);
      document.body.classList.add('verification-multi-modal');
    }
    if (e.key === ESCAPE) {
      setDisplayLearnMoreContent(false);
    }
  };

  const valueCardInfoOnClickHandler = () => {
    if (isEditable) {
      setInfoModalContent(
        getText(locId, 'customPriorityInfoModalTitle', 'customizePage')
      );
    } else {
      setInfoModalContent(infoModalContent);
    }
    setShowAdditionalInfoModal(true);
  };

  const valueCardInfoKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ENTER || e.key === SPACE) {
      if (isEditable) {
        setInfoModalContent(
          getText(locId, 'customPriorityInfoModalTitle', 'customizePage')
        );
      } else {
        setInfoModalContent(infoModalContent);
      }
      setShowAdditionalInfoModal(true);
    }
    if (e.key === ESCAPE) {
      setShowAdditionalInfoModal(false);
    }
  };

  const valueEditOnClickHandler = () => {
    setEditModalCurrentCardValue({
      statementId: valueStatementId,
      statementText: valueStatement,
    });
    setShowCustomPriorityEditModal(true);
  };

  const valueEditKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ENTER || e.key === SPACE) {
      setEditModalCurrentCardValue({
        statementId: valueStatementId,
        statementText: valueStatement,
      });
      setShowCustomPriorityEditModal(true);
    }
    if (e.key === ESCAPE) {
      setShowCustomPriorityEditModal(false);
    }
  };

  if (!isLoading) {
    return (
      <div
        className={cardClassName}
        data-testid={`valueCardFP-${valueStatementId}`}
      >
        {rank ? (
          <div className="value-rank-fp" aria-hidden="true">
            {rank}
          </div>
        ) : null}
        {(!personaTypeArrayUS.includes(userPersona) && (isEditable || infoModalContent ))? (
          <div
            role="button"
            className="value-additional-info-button"
            tabIndex={0}
            onClick={e => {
              e.stopPropagation();
              valueCardInfoOnClickHandler();
            }}
            onKeyDown={e => {
              e.stopPropagation();
              valueCardInfoKeyDownHandler(e);
            }}
          >
            <span
              className="value-edit-info--icon--custom-size"
              data-testid={`value-additional-info-button-${  valueStatementId}`}
            >
              <Icon name="help-question-mark" />
            </span>
          </div>
        ) : null}
        <div className={isEditable ? 'value-edit-img-fp' : 'value-image-fp'}>
          <img src={images[`${valueStatementId}`]} alt="" role="presentation" />
        </div>
        <div className="text-container-fp">
          {isEditable ? (
            <div
              role="button"
              className="value-edit-statement-fp"
              aria-hidden="true"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                valueEditOnClickHandler();
              }}
              onKeyDown={e => {
                e.stopPropagation();
                valueEditKeyDownHandler(e);
              }}
            >
              {valueStatement}
            </div>
          ) : (
            <div className="value-statement-fp">
              {valueStatement}
            </div>
          )}
          {displayLearnMoreButton ? (
            <div
              role="button"
              className="learn-more-button-fp"
              tabIndex={-1}
              onClick={() => {
                learnMoreClickHandler();
              }}
              onKeyDown={e => {
                learnMoreKeyDownHandler(e);
              }}
            >
              <p>{getText(locId, 'learnMoreButton', 'resultsPage')}</p>
            </div>
          ) : null}
        </div>
        <LearnMoreCards
          displayLearnMoreContent={displayLearnMoreContent}
          setDisplayLearnMoreContent={setDisplayLearnMoreContent}
          learnMoreContent={learnMoreContent}
          learnMoreLinks={learnMoreLinks}
          valueStatement={valueStatement}
          valueStatementId={valueStatementId}
          rank={rank}
        />
        {disabled || displayLearnMoreContent ? (
          <div className="value-card-disabledBox" />
        ) : null}
      </div>
    );
  }
  return <div className="value-card-fp" />;
};

ValueCardFP.defaultProps = {
  rank: null,
  isLoading: false,
  displayLearnMoreButton: false,
  disabled: false,
  learnMoreContent: null,
  cardClassName: 'value-card-fp',
  setCardClassName: null,
  learnMoreLinks: null,
  setShowAdditionalInfoModal: null,
  setShowCustomPriorityEditModal: null,
  isEditable: false,
  setEditModalCurrentCardValue: null,
  infoModalContent: null,
  setInfoModalContent: null,
};

ValueCardFP.propTypes = {
  valueStatement: PropTypes.string.isRequired,
  valueStatementId: PropTypes.number.isRequired,
  setCardClassName: PropTypes.func,
  learnMoreLinks: PropTypes.arrayOf(PropTypes.object),
  learnMoreContent: PropTypes.string,
  displayLearnMoreButton: PropTypes.bool,
  rank: PropTypes.number,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  cardClassName: PropTypes.string,
  setShowAdditionalInfoModal: PropTypes.func,
  setShowCustomPriorityEditModal: PropTypes.func,
  isEditable: PropTypes.bool,
  setEditModalCurrentCardValue: PropTypes.func,
  infoModalContent: PropTypes.string,
  setInfoModalContent: PropTypes.func,
};

export default ValueCardFP;
